import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Check, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useSquareConnection } from '@/hooks/useSquareConnection';
import { SquareLocationPickerDialog } from './SquareLocationPickerDialog';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

const OAUTH_RETURN_REASONS: Record<string, string> = {
  currency_mismatch:
    "Your Square location's currency does not match your company's country/currency. Connect a location that settles in the same currency.",
  merchant_already_connected: 'That Square account is already connected to another company.',
  no_active_locations:
    'No active Square location with card processing was found on that account.',
  storage_failed:
    "We received Square's authorization but failed to save the connection. Try again, and contact support if it persists.",
  access_denied: 'You declined the Square consent screen. No connection was created.',
  invalid_state:
    'The sign-in attempt could not be verified. Start the connection again from this page.',
  state_already_used:
    'That sign-in link was already used. Start the connection again from this page.',
  state_expired: 'The sign-in attempt timed out. Start the connection again from this page.',
  state_user_mismatch:
    'The connection was started by a different user. Start it again from your own login.',
  server_misconfigured:
    'Square is not configured on the server yet. Contact support.',
  missing_params: 'Square did not return an authorization code. Try connecting again.',
};

const ENABLES = [
  'A pay-online link on every invoice you send',
  'Automatic paid status and a job payment record when the customer pays',
  'Refunds and partial payments synced back from Square',
];

/** Settings body for the Square integration (OAuth connect, location picker, status). */
export function SquareIntegrationPanel() {
  const {
    integration,
    isForbidden,
    isLoading,
    refetch,
    connect,
    isConnecting,
    disconnect,
    isDisconnecting,
    setActiveLocation,
    isSettingLocation,
  } = useSquareConnection();
  const { formatShortDate } = useCompanyLocale();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Read the OAuth-return query string written by SquareCallback, show a
  // toast, then strip the params so a refresh doesn't re-fire it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('square');
    if (!status) return;

    if (status === 'connected') {
      toast({ title: 'Square connected' });
      if (params.get('multiple_locations') === '1') {
        setPickerOpen(true);
      }
      void refetch();
    } else if (status === 'error') {
      const reason = params.get('reason') ?? '';
      const friendly = OAUTH_RETURN_REASONS[reason] ?? (reason || 'Unknown error');
      toast({
        title: 'Square connection failed',
        description: friendly,
        variant: 'destructive',
      });
    }

    params.delete('square');
    params.delete('reason');
    params.delete('multiple_locations');
    const next = params.toString();
    const url = window.location.pathname + (next ? `?${next}` : '');
    window.history.replaceState({}, '', url);
  }, [refetch]);

  const handleConnect = async () => {
    try {
      await connect();
      // On success the page navigates away to Square — nothing else to do.
    } catch (e) {
      toast({
        title: 'Could not start Square connection',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
      toast({ title: 'Square disconnected' });
    } catch (e) {
      toast({
        title: 'Disconnect failed',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const isErrored = integration?.status === 'error' || integration?.status === 'expired';
  const locations = integration?.metadata.available_locations ?? [];
  const hasMultipleLocations = locations.length > 1;
  const hasLocation = Boolean(integration?.metadata.location_id);
  const isSandbox = integration?.environment === 'sandbox';
  const days = integration?.days_until_expiry;

  return (
    <>
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Let customers pay invoices online with a card through your own Square account.
          Payments land in your Square balance; we record them against the job automatically.
        </p>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading status...
          </div>
        ) : isForbidden ? (
          <div className="flex gap-2 rounded-lg border p-3 text-sm">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="text-muted-foreground">
              Only admins can connect or manage the Square integration.
            </div>
          </div>
        ) : !integration || integration.status === 'revoked' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Click below to authorize this app with your Square account. We only request the
              permissions needed to create invoices, take payments and see refunds.
            </p>
            <Button onClick={handleConnect} disabled={isConnecting}>
              {isConnecting ? 'Redirecting to Square...' : 'Connect Square'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {isErrored && (
              <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div>
                  <div className="font-medium">Connection needs attention</div>
                  <div className="text-muted-foreground">
                    {integration.status_message ||
                      integration.last_error ||
                      'Reconnect to restore online payments.'}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1 rounded-lg border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">
                  {integration.metadata.business_name || 'Unnamed Square account'}
                </span>
                {isSandbox && (
                  <Badge variant="outline" className="border-warning/40 bg-warning/10 text-warning">
                    Sandbox
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {hasLocation
                  ? `${integration.metadata.location_name || 'Unnamed location'} · ${integration.metadata.location_currency ?? 'Unknown currency'}`
                  : 'No location selected yet'}
              </div>
              {integration.metadata.merchant_id && (
                <div className="font-mono text-xs text-muted-foreground">
                  Merchant {integration.metadata.merchant_id}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                Connected {formatShortDate(integration.connected_at)}
                {typeof days === 'number' && (
                  <>
                    {' · '}
                    {days <= 0
                      ? 'Token expired'
                      : `Token expires in ${days} day${days === 1 ? '' : 's'}`}
                  </>
                )}
              </div>
            </div>

            {!hasLocation && hasMultipleLocations && (
              <div className="flex gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <div className="flex-1">
                  <div className="font-medium">Multiple locations available</div>
                  <div className="text-muted-foreground">
                    Pick which Square location receives invoice payments.
                  </div>
                </div>
                <Button size="sm" onClick={() => setPickerOpen(true)}>
                  Choose location
                </Button>
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t pt-2">
              {hasMultipleLocations && hasLocation && (
                <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                  Switch location
                </Button>
              )}
              {isErrored && (
                <Button variant="outline" size="sm" onClick={handleConnect} disabled={isConnecting}>
                  {isConnecting ? 'Redirecting...' : 'Reconnect'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDisconnect}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            What this enables
          </p>
          <ul className="space-y-1.5 text-sm">
            {ENABLES.map((line) => (
              <li key={line} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <span className="text-muted-foreground">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <SquareLocationPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        locations={locations}
        currentLocationId={integration?.metadata.location_id ?? null}
        onSelect={setActiveLocation}
        isSaving={isSettingLocation}
      />
    </>
  );
}
